import React, { useState, useEffect } from 'react';
import { UserProfile, FinancialConfig, Quote } from '../types';
import { ArrowLeft, Globe, TrendingUp, Info, AlertCircle, CheckCircle2, PlayCircle } from 'lucide-react';
import { generateContentWithCache } from '../lib/geminiService';
import { parseAIJSON } from '../utils/aiUtils';
import { TUTORIAL_LINKS } from '../constants';

interface Props {
  user: UserProfile;
  config: FinancialConfig;
  quotes: Quote[];
  onClose: () => void;
}

export default function RegionComparison({ user, config, quotes, onClose }: Props) {
  const [analysis, setAnalysis] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState('Analisando dados da região...');

  useEffect(() => {
    let interval: any;
    if (loading) {
      const messages = [
        'Analisando dados da região...',
        'Estudando tendências locais...',
        'Calculando médias de valor hora...',
        'Estruturando dicas estratégicas...',
        'Quase lá, finalizando a análise...'
      ];
      let idx = 0;
      setLoadingStatus(messages[0]);
      interval = setInterval(() => {
        idx = (idx + 1) % messages.length;
        setLoadingStatus(messages[idx]);
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const fetchAnalysis = async () => {
    setLoading(true);
    setAnalysis(null);

    const maxAttempts = 2;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      try {
        if (attempt === 1) {
          setLoadingStatus('Analisando dados da região...');
        } else {
          setLoadingStatus('Conexão lenta, tentando novamente...');
        }

        const lastQuote = quotes.length > 0 ? quotes[quotes.length - 1] : null;
        const prompt = `Você é um especialista em análise de mercado para microempreendedores no Brasil. 
O usuário está localizado em: ${user.city} - ${user.state}.
O nicho dele é: ${user.niche}.
O valor hora base dele é: R$ ${config.baseHourValue}.

${lastQuote ? `O último orçamento gerado foi para o serviço "${lastQuote.rawServiceName}" com preço final de R$ ${lastQuote.finalSuggestedPrice}.` : 'Ainda não há orçamentos gerados.'}

Sua tarefa é:
1. ANALISAR O MERCADO LOCAL: Estimar a média de valor hora REAL para o nicho "${user.niche}" na cidade de ${user.city}. Use inteligência de mercado baseada em tendências regionais do Brasil.
2. COMPARATIVO DIRETO: Compare o valor hora do usuário (R$ ${config.baseHourValue}) com a média estimada.
3. INSIGHTS ESTRATÉGICOS: Dê 3 dicas práticas e ESPECÍFICAS para o nicho de "${user.niche}" para ele aumentar sua percepção de valor.

IMPORTANTE: Retorne ESTRITAMENTE um objeto JSON válido no seguinte formato exato, sem marcações markdown:
{
  "media_regiao_hora": 150,
  "comparativo_percentual": 15,
  "analise_texto": "Resumo comparativo objetivo em 1 ou 2 frases...",
  "dicas": [
    "Dica prática 1",
    "Dica prática 2",
    "Dica prática 3"
  ]
}`;

        const result = await generateContentWithCache({
          model: 'gemini-3.5-flash-lite',
          contents: [{ parts: [{ text: prompt }] }],
          signal: controller.signal,
          skipCache: attempt > 1,
          config: {
            responseMimeType: 'application/json'
          }
        });
        
        const data = parseAIJSON(result.text || '', null);
        const rawMedia = data?.media_regiao_hora ?? data?.media_hora ?? data?.valor_hora_medio ?? data?.mediaRegiaoHora;

        if (data && typeof rawMedia === 'number') {
          setAnalysis({
            media_regiao_hora: rawMedia,
            comparativo_percentual: typeof data.comparativo_percentual === 'number'
              ? data.comparativo_percentual
              : (typeof data.comparativo_porcentagem === 'number' ? data.comparativo_porcentagem : 0),
            analise_texto: data.analise_texto || data.analiseTexto || data.resumo || 'Análise de mercado regional concluída.',
            dicas: Array.isArray(data.dicas) ? data.dicas : []
          });
          clearTimeout(timeoutId);
          setLoading(false);
          return;
        }
        throw new Error('Resposta sem os campos obrigatórios do schema');
      } catch (error: any) {
        clearTimeout(timeoutId);
        if (attempt === maxAttempts) {
          if (error.name === 'AbortError') {
            console.error('Analysis request timed out');
          } else {
            console.error('Error fetching comparison:', error);
          }
          setAnalysis(null);
        } else {
          console.warn(`Attempt ${attempt} failed, retrying in 1s...`, error);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAnalysis();
  }, [user, config, quotes]);

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  return (
    <div className="min-h-screen bg-zinc-50 font-sans pb-10">
      {/* Header */}
      <div className="bg-indigo-600 text-white px-6 py-8 rounded-b-4xl shadow-lg sticky top-0 z-20">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-xl font-bold">Radar de Mercado</h1>
          </div>
          <a 
            href={TUTORIAL_LINKS.radarMercado}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-xs font-bold text-white transition-colors"
          >
            <PlayCircle className="h-4 w-4" />
            Tutorial em Vídeo
          </a>
        </div>
        
        <div className="flex items-center gap-3 bg-white/10 p-4 rounded-2xl backdrop-blur-md border border-white/20">
          <Globe className="h-8 w-8 text-indigo-200" />
          <div>
            <p className="text-indigo-100 text-xs uppercase tracking-wider font-semibold">Sua Região</p>
            <p className="text-lg font-bold">{user.city} - {user.state}</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-8">
        {loading ? (
          <div className="bg-white rounded-2xl p-12 shadow-sm border border-zinc-100 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-zinc-500 font-medium">{loadingStatus}</p>
          </div>
        ) : analysis ? (
          <div className="space-y-6">
            {/* Comparison Stats */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-zinc-100">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-zinc-500 text-sm">Média na Região</p>
                  <p className="text-2xl font-bold text-zinc-900">{formatCurrency(analysis.media_regiao_hora)}/h</p>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-bold ${analysis.comparativo_percentual >= 0 ? 'bg-brand/10 text-brand' : 'bg-amber-100 text-amber-700'}`}>
                  {analysis.comparativo_percentual >= 0 ? '+' : ''}{analysis.comparativo_percentual}% vs Média
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-xl">
                  <TrendingUp className="h-5 w-5 text-indigo-600" />
                  <p className="text-sm text-zinc-700 leading-relaxed">
                    {analysis.analise_texto}
                  </p>
                </div>
              </div>
            </div>

            {/* Tips Section */}
            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
                <Info className="h-5 w-5 text-indigo-600" />
                Como cobrar mais?
              </h2>
              <div className="space-y-3">
                {(Array.isArray(analysis?.dicas) ? analysis.dicas : []).map((dica: string, i: number) => (
                  <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-zinc-100 flex gap-3">
                    <div className="bg-indigo-50 h-6 w-6 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-indigo-600 text-xs font-bold">{i + 1}</span>
                    </div>
                    <p className="text-sm text-zinc-700">{dica}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-indigo-50 p-5 rounded-3xl border border-indigo-100 flex flex-col gap-4 shadow-inner">
              <div className="flex gap-3">
                <Info className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-bold text-indigo-900 mb-1">Como calculamos isso? (Transparência)</h3>
                  <p className="text-xs text-indigo-900 leading-relaxed">
                    O R-Max analisa tendências de preços reais para o nicho de <strong className="font-bold text-indigo-900">{user.niche}</strong> na região de <strong className="font-bold text-indigo-900">{user.city}</strong>.
                  </p>
                </div>
              </div>
              
              <div className="bg-white/50 p-3 rounded-xl">
                <p className="text-[10px] text-indigo-900 leading-relaxed italic">
                  *Estes dados são estimativas estatísticas. O valor final cobrado depende da sua experiência individual, portfólio e urgência do cliente.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-8 text-center border border-zinc-100">
            <AlertCircle className="h-8 w-8 text-rose-500 mx-auto mb-3" />
            <p className="text-zinc-900 font-medium mb-1">Não foi possível carregar a análise</p>
            <p className="text-sm text-zinc-500 mb-6">Pode haver uma instabilidade na conexão com o R-Max.</p>
            <button
              onClick={fetchAnalysis}
              className="bg-indigo-600 text-white font-bold py-2 px-6 rounded-xl hover:bg-indigo-700 transition-colors"
            >
              Tentar Novamente
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
